import { sessionStorage as safeSessionStorage } from 'utils/browserStorage';
/**
 * T2-7517: shift data for talent without a verified phone.
 *
 * When the server's verifiedPhoneShiftGate switch is in enforce, the shift list
 * (requested with phoneGate=1) and other people's shift details answer 403
 * { reason: 'phone_not_verified' }. In off and monitor nothing changes, so the
 * app follows the switch through the server's answers instead of gating on its
 * own (decision D-6c).
 *
 * - Every 403 phone_not_verified that comes through Api.ts opens the existing
 *   SMS verification flow, so nothing fails silently.
 * - Background loads (the Home calendar, the shift list refreshing on focus)
 *   respect "I'll do this later" for the session. Home's calendar still shows
 *   which days have shifts: the server's answer to its request carries those
 *   dates and nothing else (phoneGateCalendarDates), and tapping a day or
 *   "View All Shifts" asks for verification (Holly, 2026-10-09). The shift list
 *   shows the "Verify your phone to see available shifts" state.
 * - After a successful verification the legacy flow dispatches
 *   PHONE_VERIFIED_EVENT and screens load shifts again.
 */
export const PHONE_NOT_VERIFIED_REASON = 'phone_not_verified';
export const PHONE_VERIFIED_EVENT = 'tempstars:phone-verified';
export const PHONE_VERIFICATION_REQUIRED_TITLE = 'Verify your phone to see available shifts';
/** Set by the legacy flow's "I'll do this later" / "not now" (src/app/verification.js). */
export const HOME_VERIFICATION_DEFER_KEY = 'ts_verification_home_deferred';

type VerificationNavigator = { goToProfile?: () => void; goToSupport?: () => void };
let navigator: VerificationNavigator = {};

/** React registers history-based navigation so the legacy modal never reloads the app (legacy-f7-modals #7). */
export function registerPhoneVerificationNavigator(next: VerificationNavigator): () => void {
  navigator = next;
  return () => {
    if (navigator === next) navigator = {};
  };
}

const reasonOf = (body: unknown): unknown => (
  body && typeof body === 'object' ? (body as { reason?: unknown }).reason : undefined
);

/** ApiError from Api.ts (body parsed) or a legacy ajax rejection (the JSON body itself). */
export function isPhoneNotVerifiedError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const candidate = error as { status?: unknown; body?: unknown; reason?: unknown };
  if (candidate.status !== undefined && candidate.status !== 403) return false;
  return reasonOf(candidate.body) === PHONE_NOT_VERIFIED_REASON || candidate.reason === PHONE_NOT_VERIFIED_REASON;
}

const CALENDAR_DATE = /^\d{4}-\d{2}-\d{2}$/;

/** The days with shifts (YYYY-MM-DD) that a phone-gate answer to Home's calendar carries; [] otherwise. */
export function phoneGateCalendarDates(error: unknown): string[] {
  if (!isPhoneNotVerifiedError(error)) return [];
  const candidate = error as { body?: unknown };
  const source = candidate.body && typeof candidate.body === 'object' ? candidate.body : error;
  const dates = (source as { dates?: unknown }).dates;
  if (!Array.isArray(dates)) return [];
  return Array.from(new Set(dates.filter((date): date is string => (
    typeof date === 'string' && CALENDAR_DATE.test(date)
  ))));
}

export function isHomeVerificationDeferred(): boolean {
  try {
    return safeSessionStorage.getItem(HOME_VERIFICATION_DEFER_KEY) === '1';
  } catch {
    return false;
  }
}

/** Opens the existing SMS verification flow; the legacy flow ignores a second call while open. */
export function requestPhoneVerification(): void {
  const verification = (window as any).TempStars?.Verification;
  if (typeof verification?.VerificationDescription !== 'function') return;
  try {
    verification.VerificationDescription(undefined, navigator.goToProfile, navigator.goToSupport);
  } catch (error) {
    console.error('[phoneVerificationGate] could not open phone verification', error);
  }
}

/** Api.ts calls this for every 403. Returns whether it was the phone gate. */
export function handlePhoneGateResponse(status: number, errorText: string, options: { background?: boolean } = {}): boolean {
  if (status !== 403) return false;
  let body: unknown = null;
  try {
    body = JSON.parse(errorText);
  } catch {
    return false;
  }
  if (reasonOf(body) !== PHONE_NOT_VERIFIED_REASON) return false;
  if (!options.background || !isHomeVerificationDeferred()) {
    requestPhoneVerification();
  }
  return true;
}

export function onPhoneVerified(listener: () => void): () => void {
  const handler = () => listener();
  window.addEventListener(PHONE_VERIFIED_EVENT, handler);
  return () => window.removeEventListener(PHONE_VERIFIED_EVENT, handler);
}
